import { useEffect } from "react"

// ** Third Party Components
import { FolderOpen, HardDrive, Calendar, RefreshCw, AlertTriangle } from "lucide-react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  LabelList,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts"

// ** UI Components
import { Button } from "@components/common/button"
import { Card, CardContent, CardHeader } from "@components/common/card"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@components/common/chart"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Helpers
import {
  formatFileSize,
  getDrillDownSubtitle,
  getDrillDownTitle,
  getFileExtension,
} from "@pma/utils/helpers"

// ** Types
import { CommonAnalyticsProps } from "@pma/types"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"
import { useDrillDown } from "@hooks/use-drill-down"
import { useFileQueries } from "@pma/hooks/use-file-queries"
import { useFileComputed } from "@pma/hooks/use-file-computed"
import { KpiCard, ChartCard, DrillDownSheet, DrillDownTable } from "../widgets"

export function FileAnalytics({ filters, onCountLoaded }: CommonAnalyticsProps) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id

  const { filesRaw, usedSpaceRaw, projectsRaw, isLoading, filesError, refetchFiles } =
    useFileQueries(orgId)

  const { state: drillDown, open: openDrillDown, close: closeDrillDown } = useDrillDown()

  const {
    files,
    projectNameMap,
    totalFiles,
    totalSizeBytes,
    filesThisMonth,
    fileTypeDonut,
    storageByProject,
    uploadsByUser,
  } = useFileComputed({ filesRaw, usedSpaceRaw, projectsRaw, filters })

  // Report count to parent for tab badge
  useEffect(() => {
    if (onCountLoaded && totalFiles > 0) onCountLoaded(totalFiles)
  }, [totalFiles, onCountLoaded])

  // Error state
  if (filesError) {
    return (
      <div
        className='flex flex-col items-center justify-center gap-4 py-12'
        dir={isHebrew ? "rtl" : "ltr"}
      >
        <AlertTriangle className='h-10 w-10 text-red-500' />
        <p className='text-sm text-muted-foreground'>{(filesError as Error)?.message}</p>
        <Button variant='outline' size='sm' onClick={() => refetchFiles()}>
          <RefreshCw className='mr-2 h-4 w-4' /> {translate("Retry")}
        </Button>
      </div>
    )
  }

  // Loading state
  if (isLoading)
    return (
      <div className='flex flex-col gap-6 py-4' dir={isHebrew ? "rtl" : "ltr"}>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {[...Array(3)].map((_, i) => (
            <Card className='relative animate-pulse overflow-hidden border border-gray-100 bg-gradient-to-br from-gray-50 to-white shadow-sm'>
              <CardContent className='flex flex-col gap-3 p-4'>
                <div className='flex items-center justify-between'>
                  <div className='h-9 w-9 rounded-lg bg-gray-200' />
                  <div className='h-5 w-12 rounded-full bg-gray-200' />
                </div>
                <div className='space-y-1.5'>
                  <div className='h-8 w-16 rounded bg-gray-200' />
                  <div className='h-3 w-24 rounded bg-gray-200' />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <Card className="animate-pulse border border-gray-100 shadow-sm">
            <CardHeader className='pb-2'>
              <div className='h-4 w-40 rounded bg-gray-200' />
            </CardHeader>
            <CardContent className='flex h-[280px] items-center justify-center'>
              <div className='h-40 w-40 rounded-full border-[24px] border-gray-100 bg-gray-200' />
            </CardContent>
          </Card>
          <Card className="animate-pulse border border-gray-100 shadow-sm">
            <CardHeader className='pb-2'>
              <div className='h-4 w-40 rounded bg-gray-200' />
            </CardHeader>
            <CardContent className='flex h-[280px] items-center justify-center'>
              <div className='h-40 w-40 rounded-full border-[24px] border-gray-100 bg-gray-200' />
            </CardContent>
          </Card>
        </div>
      </div>
    )

  // Empty state
  if (totalFiles === 0) {
    return (
      <div
        className='flex flex-col items-center justify-center py-20 text-center'
        dir={isHebrew ? "rtl" : "ltr"}
      >
        <FolderOpen className='mb-4 h-16 w-16 text-gray-300' />
        <h3 className='text-lg font-medium text-gray-900'>{translate("No Files Yet")}</h3>
        <p className='mt-2 text-sm text-muted-foreground'>
          {translate("Upload files in your projects to see analytics")}
        </p>
      </div>
    )
  }

  // Chart configs
  const typeConfig: ChartConfig = {
    count: { label: translate("Files"), color: "hsl(var(--chart-1))" },
  }
  fileTypeDonut.forEach((d) => {
    typeConfig[d.title] = { label: d.title, color: d.fill }
  })

  const projectStorageConfig: ChartConfig = {
    value: { label: "MB", color: "hsl(var(--chart-1))" },
  }
  storageByProject.forEach((d) => {
    projectStorageConfig[d.name] = { label: d.name, color: d.fill }
  })

  const userConfig: ChartConfig = {
    count: { label: translate("Files"), color: "hsl(var(--chart-1))" },
  }
  uploadsByUser.forEach((d) => {
    userConfig[d.name] = { label: d.name, color: d.fill }
  })

  return (
    <div className='flex flex-col gap-6 py-4' dir={isHebrew ? "rtl" : "ltr"}>
      {/* KPI Cards */}
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        <KpiCard
          label={translate("Total Files")}
          value={totalFiles}
          icon={FolderOpen}
          color='blue'
        />
        <KpiCard
          label={translate("Total Storage")}
          value={formatFileSize(totalSizeBytes)}
          icon={HardDrive}
          color='purple'
        />
        <KpiCard
          label={translate("Files This Month")}
          value={filesThisMonth}
          icon={Calendar}
          color='green'
        />
      </div>

      {/* Charts row */}
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
        {/* File Type Donut */}
        {fileTypeDonut.length > 0 && (
          <ChartCard
            title={translate("Files by Type")}
            description={translate("Distribution by file extension")}
          >
            <ChartContainer
              config={typeConfig}
              className='mx-auto h-[280px] w-full'
              dir={isHebrew ? "rtl" : "ltr"}
            >
              <PieChart>
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      formatter={(value, name) => {
                        const pct =
                          totalFiles > 0 ? Math.round((Number(value) / totalFiles) * 100) : 0
                        return [`${Number(value).toLocaleString()} (${pct}%)`, name]
                      }}
                    />
                  }
                />
                <Pie
                  data={fileTypeDonut}
                  dataKey='count'
                  nameKey='title'
                  innerRadius='42%'
                  outerRadius='68%'
                  strokeWidth={3}
                  stroke='white'
                  className='cursor-pointer'
                  onClick={(data: any) => {
                    const ext = data?.title || data?.payload?.title
                    if (ext) openDrillDown({ type: "file-type", extension: ext })
                  }}
                >
                  {fileTypeDonut.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                        return (
                          <text
                            x={viewBox.cx}
                            y={viewBox.cy}
                            textAnchor='middle'
                            dominantBaseline='middle'
                          >
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) - 8}
                              className='fill-foreground'
                              style={{
                                fontSize: 28,
                                fontWeight: 700,
                                fontFamily: "ui-monospace, monospace",
                              }}
                            >
                              {totalFiles.toLocaleString()}
                            </tspan>
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) + 16}
                              className='fill-muted-foreground'
                              style={{ fontSize: 11 }}
                            >
                              {translate("Files")}
                            </tspan>
                          </text>
                        )
                      }
                    }}
                  />
                </Pie>
                <ChartLegend
                  className='mt-2 flex flex-wrap gap-2'
                  content={<ChartLegendContent />}
                />
              </PieChart>
            </ChartContainer>
          </ChartCard>
        )}

        {/* Storage per Project */}
        {storageByProject.length > 0 && (
          <ChartCard
            title={translate("Storage by Project")}
            description={translate("Megabytes per project")}
          >
            <ChartContainer config={projectStorageConfig} className='h-[280px] w-full'>
              <BarChart data={storageByProject} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray='3 3' stroke='#f0f0f0' />
                <XAxis
                  dataKey='name'
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  reversed={isHebrew}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => (val.length > 10 ? val.slice(0, 10) + "…" : val)}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  orientation={isHebrew ? "right" : "left"}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => `${val} MB`}
                />
                <ChartTooltip
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  content={
                    <ChartTooltipContent
                      formatter={(value: any) => `${Number(value).toFixed(1)} MB`}
                    />
                  }
                />
                <Bar
                  dataKey='value'
                  radius={[6, 6, 0, 0]}
                  barSize={36}
                  className='cursor-pointer'
                  onClick={(data: any) => {
                    const entry = data?.payload || data
                    if (entry?.projectId) {
                      openDrillDown({
                        type: "file-project",
                        projectId: entry.projectId,
                        projectName: entry.name,
                      })
                    }
                  }}
                >
                  {storageByProject.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                  <LabelList
                    dataKey='value'
                    position='top'
                    style={{ fontSize: 11, fontWeight: 600, fill: "#374151" }}
                    formatter={(val: number) => `${val} MB`}
                  />
                </Bar>
              </BarChart>
            </ChartContainer>
          </ChartCard>
        )}
      </div>

      {/* Drill-Down Sheet */}
      <DrillDownSheet
        open={!!drillDown}
        onClose={closeDrillDown}
        title={getDrillDownTitle(drillDown, translate)}
        subtitle={getDrillDownSubtitle(drillDown, translate)}
        width='wide'
      >
        {drillDown?.type === "file-type" &&
          (() => {
            const ext = drillDown.extension.toLowerCase()
            const rows = files
              .filter(
                (f: any) => getFileExtension(f.name || f.type || "unknown") === ext.toLowerCase(),
              )
              .map((f: any) => ({
                name: f.name || "—",
                project: projectNameMap[f.project_id] || `Project ${f.project_id || "?"}`,
                size: Number(f.size) || 0,
                sizeFormatted: formatFileSize(Number(f.size) || 0),
                uploadedBy: f._created_by?.name || f.created_by_name || "—",
                date: f.created_at
                  ? new Date(f.created_at).toLocaleDateString(isHebrew ? "he-IL" : "en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "—",
                _rawDate: new Date(f.created_at || 0).getTime(),
              }))
              .sort((a, b) => b._rawDate - a._rawDate)
            return (
              <DrillDownTable
                columns={[
                  { header: translate("Name"), accessor: "name", sortable: true },
                  { header: translate("Project"), accessor: "project", sortable: true },
                  {
                    header: translate("Size"),
                    accessor: "size",
                    sortable: true,
                    render: (_v: number, row: any) => row.sizeFormatted,
                  },
                  {
                    header: translate("Uploaded By"),
                    accessor: "uploadedBy",
                    sortable: true,
                  },
                  { header: translate("Date"), accessor: "date", sortable: true },
                ]}
                data={rows}
              />
            )
          })()}
        {drillDown?.type === "file-project" &&
          (() => {
            const rows = files
              .filter((f: any) => f.project_id === drillDown.projectId)
              .map((f: any) => ({
                name: f.name || "—",
                project: projectNameMap[f.project_id] || `Project ${f.project_id || "?"}`,
                size: Number(f.size) || 0,
                sizeFormatted: formatFileSize(Number(f.size) || 0),
                uploadedBy: f._created_by?.name || f.created_by_name || "—",
                date: f.created_at
                  ? new Date(f.created_at).toLocaleDateString(isHebrew ? "he-IL" : "en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "—",
                _rawDate: new Date(f.created_at || 0).getTime(),
              }))
              .sort((a, b) => b._rawDate - a._rawDate)
            return (
              <DrillDownTable
                columns={[
                  { header: translate("Name"), accessor: "name", sortable: true },
                  { header: translate("Project"), accessor: "project", sortable: true },
                  {
                    header: translate("Size"),
                    accessor: "size",
                    sortable: true,
                    render: (_v: number, row: any) => row.sizeFormatted,
                  },
                  {
                    header: translate("Uploaded By"),
                    accessor: "uploadedBy",
                    sortable: true,
                  },
                  { header: translate("Date"), accessor: "date", sortable: true },
                ]}
                data={rows}
              />
            )
          })()}
      </DrillDownSheet>

      {/* Uploads per User */}
      {uploadsByUser.length > 0 && (
        <ChartCard
          title={translate("Uploads by User")}
          description={translate("File uploads per team member")}
        >
          <ChartContainer config={userConfig} className='h-[280px] w-full'>
            <BarChart
              data={uploadsByUser}
              layout='vertical'
              margin={{ top: 8, right: 40, left: 0, bottom: 0 }}
            >
              <CartesianGrid horizontal={false} strokeDasharray='3 3' stroke='#f0f0f0' />
              <XAxis
                type='number'
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "#6b7280" }}
                allowDecimals={false}
              />
              <YAxis
                type='category'
                dataKey='name'
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "#6b7280" }}
                width={100}
                tickFormatter={(val) => (val.length > 14 ? val.slice(0, 14) + "…" : val)}
              />
              <ChartTooltip
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
                content={<ChartTooltipContent />}
              />
              <Bar dataKey='count' radius={[0, 6, 6, 0]} barSize={24}>
                {uploadsByUser.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
                <LabelList
                  dataKey='count'
                  position='right'
                  style={{ fontSize: 11, fontWeight: 600, fill: "#374151" }}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </ChartCard>
      )}
    </div>
  )
}

export default FileAnalytics
